/* Ardmore country field v2. Replace the previous asset, then reload the page.
   Keeps Omnisend's original options and selection handlers as the source of truth.
   Applies only to the two signup forms below. No popup/trigger changes. */
(() => {
  'use strict';

  if (window.ardmoreCountryFieldV2) return;
  window.ardmoreCountryFieldV2 = true;

  const ids = [
    '6adee39411ab1821b7b0c434',
    '6adee34011ab1821b7b0c433'
  ];

  const popular = [
    ['US', 'United States'],
    ['GB', 'United Kingdom'],
    ['ZA', 'South Africa'],
    ['AU', 'Australia']
  ];

  const selector = ids.map(id =>
    `[id^="omnisend-form-${id}-"][role="combobox"][name="country"]`
  ).join(',');

  const mounted = new Map();
  const touched = new Set();

  const normalise = value =>
    value.normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  let detection;

  function detectCountry() {
    if (!detection) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);

      detection = fetch('/browsing_context_suggestions.json', {
        credentials: 'same-origin',
        signal: controller.signal
      })
        .then(response => response.ok ? response.json() : null)
        .then(data =>
          String(data?.detected_values?.country?.handle || '').toUpperCase()
        )
        .catch(() => '')
        .finally(() => clearTimeout(timeout));
    }

    return detection;
  }

  function start() {
    const style = document.createElement('style');

    style.textContent = `
      .ardmore-country-native {
        display: none !important;
      }

      .ardmore-country {
        position: relative;
        width: 100%;
        font: 400 16px/1.4 Raleway, sans-serif;
        color: #121212;
      }

      .ardmore-country input {
        box-sizing: border-box !important;
        width: 100% !important;
        height: 48px !important;
        min-width: 0 !important;
        margin: 0 !important;
        padding: 12px 40px 12px 14px !important;
        border: 1px solid #d9d9d9 !important;
        border-radius: 0 !important;
        background: #fff !important;
        color: #121212 !important;
        font: inherit !important;
      }

      .ardmore-country input:focus {
        outline: 2px solid #121212 !important;
        outline-offset: 1px;
      }

      .ardmore-country::after {
        content: '';
        position: absolute;
        right: 17px;
        top: 19px;
        width: 7px;
        height: 7px;
        border-right: 1px solid;
        border-bottom: 1px solid;
        transform: rotate(45deg);
        pointer-events: none;
      }

      .ardmore-country-panel {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        z-index: 2147483647;
        max-height: 240px;
        max-height: min(240px, 35dvh);
        overflow-y: auto;
        overscroll-behavior: contain;
        background: #fff;
        border: 1px solid #d9d9d9;
        box-shadow: 0 4px 12px #0002;
      }

      .ardmore-country-panel[hidden] {
        display: none !important;
      }

      .ardmore-country-option {
        padding: 10px 14px;
        cursor: pointer;
      }

      .ardmore-country-option[data-active="true"] {
        background: #f2f2f2;
        outline: 1px solid #d9d9d9;
        outline-offset: -1px;
      }

      .ardmore-country-option[aria-selected="true"] {
        font-weight: 600;
      }

      .ardmore-country-heading {
        padding: 10px 14px 6px;
        font-size: 11px;
        letter-spacing: 1px;
        text-transform: uppercase;
        color: #727272;
      }

      .ardmore-country-heading--all {
        border-top: 1px solid #d9d9d9;
      }

      .ardmore-country-empty {
        padding: 12px 14px;
        color: #727272;
      }
    `;

    document.head.append(style);

    function enhance(combo) {
      const list = document.getElementById(
        combo.getAttribute('aria-controls')
      );

      if (!list || mounted.get(combo)?.list === list) return;

      mounted.get(combo)?.destroy();
      mounted.delete(combo);

      const nativeOptions = () =>
        [...list.querySelectorAll('[role="option"][data-value]')]
          .filter(el => !el.hasAttribute('data-ardmore-country-shortcut'));

      if (!nativeOptions().length) return;

      const formId = ids.find(id =>
        combo.id.startsWith(`omnisend-form-${id}-`)
      );

      const abort = new AbortController();

      const on = (el, type, fn, capture = false) =>
        el.addEventListener(type, fn, {
          signal: abort.signal,
          capture
        });

      const root = document.createElement('div');
      root.className = 'ardmore-country';

      const input = document.createElement('input');
      input.type = 'text';
      input.id = `${combo.id}-search`;
      input.autocomplete = 'off';
      input.spellcheck = false;
      input.placeholder = 'Choose your country';
      input.setAttribute('role', 'combobox');
      input.setAttribute(
        'aria-label',
        combo.getAttribute('aria-label') || 'Country'
      );
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-haspopup', 'listbox');
      input.setAttribute('aria-expanded', 'false');

      const panel = document.createElement('div');
      panel.className = 'ardmore-country-panel';
      panel.id = `${input.id}-list`;
      panel.setAttribute('role', 'listbox');
      panel.setAttribute('aria-label', 'Countries');
      panel.hidden = true;

      input.setAttribute('aria-controls', panel.id);

      root.append(input, panel);
      combo.after(root);

      combo.classList.add('ardmore-country-native');
      list.classList.add('ardmore-country-native');

      let open = false;
      let active = -1;
      let rows = [];
      let selected = '';

      const countries = () => nativeOptions().map(el => ({
        code: el.dataset.value,
        label: el.textContent.trim()
      }));

      const selectedNative = () =>
        nativeOptions().find(el =>
          el.getAttribute('aria-selected') === 'true'
        )?.dataset.value || '';

      function sync() {
        selected = selectedNative();

        if (!open) {
          input.value =
            countries().find(item => item.code === selected)?.label || '';
        }

        for (const attr of [
          'aria-required',
          'aria-invalid',
          'aria-describedby',
          'aria-labelledby'
        ]) {
          const value = combo.getAttribute(attr);

          if (value !== null) {
            input.setAttribute(attr, value);
          } else {
            input.removeAttribute(attr);
          }
        }

        rows.forEach(row => {
          row.setAttribute(
            'aria-selected',
            String(row.dataset.code === selected)
          );
        });
      }

      function highlight(index) {
        active = rows.length
          ? Math.max(0, Math.min(index, rows.length - 1))
          : -1;

        rows.forEach((row, i) => {
          row.dataset.active = String(i === active);
        });

        if (active >= 0) {
          input.setAttribute('aria-activedescendant', rows[active].id);

          const row = rows[active];

          if (row.offsetTop < panel.scrollTop) {
            panel.scrollTop = row.offsetTop;
          } else if (
            row.offsetTop + row.offsetHeight >
            panel.scrollTop + panel.clientHeight
          ) {
            panel.scrollTop =
              row.offsetTop + row.offsetHeight - panel.clientHeight;
          }
        } else {
          input.removeAttribute('aria-activedescendant');
        }
      }

      function render(query = '') {
        panel.replaceChildren();
        rows = [];

        const all = countries();
        const term = normalise(query);

        const heading = (text, suffix = '') => {
          const el = document.createElement('div');
          el.className = `ardmore-country-heading ${suffix}`;
          el.setAttribute('role', 'presentation');
          el.textContent = text;
          panel.append(el);
        };

        const add = (item, label = item.label) => {
          const row = document.createElement('div');
          row.className = 'ardmore-country-option';
          row.id = `${input.id}-option-${rows.length}`;
          row.dataset.code = item.code;
          row.setAttribute('role', 'option');
          row.setAttribute(
            'aria-selected',
            String(item.code === selected)
          );
          row.textContent = label;

          rows.push(row);
          panel.append(row);
        };

        if (!term) {
          const pinned = popular.filter(([code]) =>
            all.some(item => item.code === code)
          );

          if (pinned.length) {
            heading('Most popular');

            pinned.forEach(([code, label]) => {
              add(all.find(item => item.code === code), label);
            });
          }

          heading('All countries', 'ardmore-country-heading--all');
          all.forEach(item => add(item));
        } else {
          all.filter(item =>
            normalise(
              `${item.label} ${item.code} ` +
              `${item.code === 'GB' ? 'UK' : ''} ` +
              `${item.code === 'US' ? 'USA' : ''}`
            ).includes(term)
          ).forEach(item => add(item));
        }

        if (!rows.length) {
          const empty = document.createElement('div');
          empty.className = 'ardmore-country-empty';
          empty.setAttribute('role', 'status');
          empty.textContent = 'No countries found';
          panel.append(empty);
        }

        panel.scrollTop = 0;
        highlight(term && rows.length ? 0 : -1);
      }

      function show() {
        touched.add(formId);

        if (open) return;

        open = true;
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        input.value = '';
        render();
      }

      function close() {
        open = false;
        panel.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        sync();
      }

      function choose(code, manual) {
        if (manual) touched.add(formId);

        const option = nativeOptions().find(el =>
          el.dataset.value === code
        );

        if (!option) return;

        // Invoke Omnisend's own selection handler; never invent country values.
        option.click();
        close();

        if (manual) input.focus({preventScroll: true});

        queueMicrotask(sync);
      }

      on(input, 'click', show);

      on(input, 'input', () => {
        touched.add(formId);

        const query = input.value;

        if (!open) show();

        input.value = query;
        render(query);
      });

      on(input, 'focus', () => touched.add(formId));

      on(panel, 'pointerdown', event => event.preventDefault());

      on(panel, 'click', event => {
        const row = event.target.closest('[data-code]');

        if (row) {
          event.preventDefault();
          event.stopPropagation();
          choose(row.dataset.code, true);
        }
      });

      on(panel, 'pointermove', event => {
        const row = event.target.closest('[data-code]');

        if (row) highlight(rows.indexOf(row));
      });

      // Capture Escape before the popup's usual bubbling key handlers.
      on(document, 'keydown', event => {
        if (event.target !== input) return;

        if (event.key === 'Escape' && open) {
          event.preventDefault();
          event.stopImmediatePropagation();
          close();
          return;
        }

        if (event.key === 'Tab') {
          close();
          return;
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          event.stopImmediatePropagation();

          if (!open) show();

          highlight(
            active < 0
              ? (event.key === 'ArrowDown' ? 0 : rows.length - 1)
              : active + (event.key === 'ArrowDown' ? 1 : -1)
          );
        } else if (event.key === 'Enter') {
          event.preventDefault();
          event.stopImmediatePropagation();

          if (!open) {
            show();
          } else if (active >= 0) {
            choose(rows[active].dataset.code, true);
          }
        }
      }, true);

      on(document, 'pointerdown', event => {
        if (open && !root.contains(event.target)) close();
      }, true);

      on(root, 'focusout', () => {
        queueMicrotask(() => {
          if (!root.contains(document.activeElement)) close();
        });
      });

      // Preserve labels and native validation attempting to focus the original field.
      on(combo, 'focus', () => input.focus());

      const observer = new MutationObserver(() => {
        sync();

        if (open) render(input.value);
      });

      observer.observe(list, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['aria-selected']
      });

      observer.observe(combo, {
        attributes: true,
        attributeFilter: [
          'aria-invalid',
          'aria-describedby',
          'aria-required',
          'data-value'
        ]
      });

      mounted.set(combo, {
        list,
        root,
        destroy() {
          abort.abort();
          observer.disconnect();
          root.remove();
          combo.classList.remove('ardmore-country-native');
          list.classList.remove('ardmore-country-native');
        }
      });

      sync();

      detectCountry().then(code => {
        if (
          !root.isConnected ||
          touched.has(formId) ||
          selectedNative() ||
          open ||
          !code
        ) return;

        choose(code, false);
      });
    }

    let queued = false;

    function scan() {
      queued = false;

      for (const [combo, instance] of mounted) {
        if (
          !combo.isConnected ||
          !instance.list.isConnected ||
          !instance.root.isConnected
        ) {
          instance.destroy();
          mounted.delete(combo);
        }
      }

      document.querySelectorAll(selector).forEach(enhance);
    }

    new MutationObserver(() => {
      if (!queued) {
        queued = true;
        requestAnimationFrame(scan);
      }
    }).observe(document.body, {
      childList: true,
      subtree: true
    });

    scan();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {once: true});
  } else {
    start();
  }
})();